import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';

/**
 * Shown after login when a guest draft (written while signed out) was rejected
 * by the server for good, e.g. a 422. Such a draft used to be retried at every
 * sign-in, for ever, with nothing shown (#227). It's now quarantined on this
 * device, and the user decides: try again, discard it, or decide later.
 *
 * Waits for GuestReviewConflictModal to finish, so the two never stack.
 * There's deliberately no Edit (decided 2026-10-23): the failures seen so far
 * can't be fixed by editing text, and the draft's text is shown here.
 */
function GuestDraftFailureModal() {
  const {
    guestReviewConflicts, guestDraftFailures, retryFailedDraft, discardFailedDraft, dismissDraftFailures,
  } = useAuth();
  const [busy, setBusy] = useState(false);

  const failure = guestDraftFailures && guestDraftFailures[0];
  if (!failure || (guestReviewConflicts && guestReviewConflicts.length)) return null;

  const { kind, draft } = failure;
  const what = kind === 'visits' ? 'visit' : 'review';
  const targetName = draft?.target?.name || 'this item';
  const text = kind === 'visits' ? draft.notes : draft.review_text;
  const reason = draft?.last_error?.detail;
  const remaining = guestDraftFailures.length;

  const tryAgain = async () => {
    setBusy(true);
    await retryFailedDraft(failure);
    setBusy(false);
  };

  return (
    <Modal show onHide={dismissDraftFailures} centered>
      <Modal.Header closeButton>
        <Modal.Title>A {what} draft couldn't be posted</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          While signed out, you saved a {what} for <strong>{targetName}</strong>. We tried
          to post it to your account, but it was turned down
          {reason ? <>: <em>{reason}</em></> : '.'}
        </p>
        {text && (
          <blockquote className="border-start ps-3 text-muted small mb-2">“{text}”</blockquote>
        )}
        <p className="text-muted small mb-0">
          It's kept on this device and won't be retried on its own.
          {remaining > 1 && ` ${remaining - 1} more after this one.`}
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="link" className="me-auto" disabled={busy} onClick={dismissDraftFailures}>
          Not now
        </Button>
        <Button variant="outline-danger" disabled={busy} onClick={() => discardFailedDraft(failure)}>
          Discard
        </Button>
        <Button variant="primary" disabled={busy} onClick={tryAgain}>
          {busy ? 'Trying…' : 'Try again'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default GuestDraftFailureModal;
